<template>
  <Intersect v-editable="blok" :threshold="0.5" @enter.once="handleVisibility">
    <div
      v-if="desktopImage?.filename || mobileImage?.filename"
      class="relative inset-0 flex flex-col items-center justify-end"
      :class="[
        { 'w-full': isFullWidth, '!mt-0': overrideMarginTop },
        marginClasses,
      ]"
    >
      <div v-if="showLink" class="size-full max-w-full overflow-hidden">
        <!-- Passing the cached_url as processedUrl, rest is just blok.link -->
        <CMSLink
          :cms-link="{ ...blok.link, cached_url: processedUrl }"
          :product-id="blok.product_id"
          :order-number="blok.order_number"
          class="group flex size-full items-center justify-center"
          :aria-label="ctaLinkAriaLabel"
          @click="handleClick(blok.link!, $event)"
        >
          <!-- Desktop crop (>= lg) and mobile crop (< lg) toggled via CSS only -->
          <NuxtLazyImage
            v-if="desktopImage?.filename"
            :source="desktopImage.filename"
            :alt="desktopImage.alt || imageWrapper?.altDescription"
            :optimise-desktop="optimiseDesktop"
            media="desktop"
            class="hidden !w-full group-focus:ring-2 group-focus:ring-gray-secondary lg:block"
          />
          <NuxtLazyImage
            v-if="mobileImage?.filename"
            :source="mobileImage.filename"
            :alt="mobileImage.alt || imageWrapper?.altDescription"
            :optimise-desktop="optimiseDesktop"
            media="mobile"
            class="block !w-full group-focus:ring-2 group-focus:ring-gray-secondary lg:hidden"
          />
        </CMSLink>
      </div>
      <template v-else>
        <!-- Desktop crop (>= lg) and mobile crop (< lg) toggled via CSS only -->
        <NuxtLazyImage
          v-if="desktopImage?.filename"
          :source="desktopImage.filename"
          :alt="desktopImage.alt || imageWrapper?.altDescription"
          :optimise-desktop="optimiseDesktop"
          media="desktop"
          class="hidden !w-full group-focus:ring-2 group-focus:ring-gray-secondary lg:block"
        />
        <NuxtLazyImage
          v-if="mobileImage?.filename"
          :source="mobileImage.filename"
          :alt="mobileImage.alt || imageWrapper?.altDescription"
          :optimise-desktop="optimiseDesktop"
          media="mobile"
          class="block !w-full group-focus:ring-2 group-focus:ring-gray-secondary lg:hidden"
        />
      </template>
      <div
        v-if="!buttonInvisible"
        class="pointer-events-none absolute bottom-10 flex w-1/2 flex-col items-center justify-between"
        :class="{
          '!bottom-2': overrideMarginTop || affectCtaButtonPositioning,
        }"
      >
        <h2
          v-if="!isInFlyoutMenu"
          :class="[
            'mb-10 text-center font-medium text-white',
            'text-lg leading-[32px]',
            '[@media(min-width:1520px)]:text-lg [@media(min-width:1520px)]:leading-[36px]',
            '[@media(min-width:1680px)]:text-xl [@media(min-width:1680px)]:leading-[40px]',
            '[@media(min-width:1800px)]:text-2xl [@media(min-width:1800px)]:leading-[46px]',
          ]"
        >
          {{ blok.headline }}
        </h2>
        <CtaButton
          v-if="ctaButton"
          :blok="ctaButton"
          @click="
            handleClick(ctaButton!.ctaLink as InteractableImageLink, $event)
          "
        />
      </div>
    </div>
  </Intersect>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import type { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { getTargetBlankLabel } from '#layers/base/utils/a11y'
import useRouteHelpers from '#layers/base/composables/useRouteHelpers'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import type { SbInteractableImage } from '~/cms/storyblok/types/storyblok.d.ts'
import useStoryblokMargins from '~/cms/storyblok/composables/useStoryblokMargins'
import locales from '~/config/locales'
import { resolveTilePromoType } from '~/utils/tracking'
import {
  CTA_PROMOTION_CONTEXT_KEY,
  IS_IN_FLYOUT_MENU_KEY,
} from '#layers/base/constants/injectionKeys'
import type { TrackPromotionPageType } from '~/types/tracking'
import type { StoryLink } from '~/@types/storyblok'

// For some reason the story is missing from the link, so we need to add it manually
type InteractableImageLink = SbInteractableImage['link'] & { story?: StoryLink }

export default defineComponent({
  props: {
    blok: {
      type: Object as PropType<SbInteractableImage>,
      default: () => {},
    },
    promotionName: {
      type: String,
      default: 'interactable_image',
    },
    isFullWidth: {
      type: Boolean,
      default: true,
    },
    optimiseDesktop: {
      type: Boolean,
      default: true,
    },
    overrideMarginTop: {
      type: Boolean,
      default: false,
    },
    affectCtaButtonPositioning: {
      type: Boolean,
      defaukt: false,
    },
    siblingIndex: {
      type: Number,
      default: 0,
    },
  },
  emits: ['click'],
  setup({ blok, siblingIndex }, { emit }) {
    const { trackPromotionSelect, trackPromotionView } = useTrackingEvents()
    const { toLocalePath } = useRouteHelpers()
    const imageWrapper = blok?.Image?.[0]
    const { marginClasses } = useStoryblokMargins(blok)
    const { t } = useI18n()
    const isInFlyoutMenu = inject(IS_IN_FLYOUT_MENU_KEY, false)

    // This block emits the promotion events for the CTA it nests, using its own
    // parameters, so the CTA must not emit competing ones. `claimedBy` is
    // diagnostic only — the real promoType is resolved per link at click time.
    provide(CTA_PROMOTION_CONTEXT_KEY, { claimedBy: 'interactableimage' })

    // Crop selection is driven by CSS media queries in the template (see the
    // `hidden lg:block` / `block lg:hidden` toggle), not by device/viewport JS.
    // This keeps the rendered image identical on server and client, so it is
    // correct on real devices regardless of UA/CDN detection and never sticks
    // on the wrong crop after hydration. Each crop falls back to the other when
    // the CMS entry only provides one asset.
    const desktopImage = computed(() =>
      imageWrapper?.image?.filename
        ? imageWrapper.image
        : imageWrapper?.imageMobile,
    )
    const mobileImage = computed(() =>
      imageWrapper?.imageMobile?.filename
        ? imageWrapper.imageMobile
        : imageWrapper?.image,
    )

    const showLink = computed(() => {
      // cast the return value from a number to a boolean
      return !!(
        blok.link?.cached_url ||
        blok.product_id?.length ||
        blok.order_number?.length
      )
    })

    const ctaButton = computed(() =>
      (blok.CtaButton || []).length > 0 ? blok.CtaButton?.[0] : undefined,
    )

    const ctaLinkAriaLabel = computed(() => {
      const label =
        blok.headline ||
        desktopImage.value?.alt ||
        mobileImage.value?.alt ||
        imageWrapper?.altDescription
      return getTargetBlankLabel(label || '', t)
    })

    const imagePosition = computed<string | undefined>(() => {
      if (!imageWrapper) {
        return undefined
      }

      let horizontalAlignment: 'left' | 'right' | 'center'
      if (imageWrapper.horizontal_alignment === 'left') {
        horizontalAlignment = 'left'
      } else if (imageWrapper.horizontal_alignment === 'right') {
        horizontalAlignment = 'right'
      } else {
        horizontalAlignment = 'center'
      }

      let verticalAlignment: 'center' | 'bottom' | 'top'
      if (imageWrapper.vertical_alignment === 'centered') {
        verticalAlignment = 'center'
      } else if (imageWrapper.vertical_alignment === 'bottom') {
        verticalAlignment = 'bottom'
      } else {
        verticalAlignment = 'top'
      }

      return verticalAlignment === 'center' && horizontalAlignment === 'center'
        ? 'center'
        : `${horizontalAlignment} ${verticalAlignment}`
    })

    const getPromotionTrackingParams = (link?: InteractableImageLink) => {
      const promoType = resolveTilePromoType(link?.story)

      return {
        id: blok.promotion_id || 'interactable_image_promotion_id',
        name: blok.promotion_name || 'interactable_image_promotion_name',
        pageType: isInFlyoutMenu
          ? ('nav' as TrackPromotionPageType)
          : undefined,
        promoType: isInFlyoutMenu ? 'card' : promoType,
        position: siblingIndex + 1,
        creativeNameSb: blok.creative_name || '',
      }
    }

    return {
      isInFlyoutMenu,
      buttonInvisible: computed(() => {
        const button = blok.CtaButton?.[0]
        return !button || !!button.invisible
      }),
      link: computed(() => {
        return (
          (blok?.link?.cached_url &&
            toLocalePath(blok?.link?.cached_url || '/')) ||
          undefined
        )
      }),
      imageWrapper,
      desktopImage,
      mobileImage,
      ctaButton,
      ctaLinkAriaLabel,
      imagePosition,
      handleClick: (link: InteractableImageLink, event: MouseEvent) => {
        trackPromotionSelect(getPromotionTrackingParams(link))

        emit('click', event)
      },
      handleVisibility: () => {
        // If the image link is shown, use the image link, otherwise use the cta button link
        const link = showLink.value ? blok.link : ctaButton.value?.ctaLink
        trackPromotionView(getPromotionTrackingParams(link))
      },
      // this computed property checks if the `cached_url` is a locale (e.g., "/fr/")
      // if so, it converts it back to an empty string to ensure CMSLink points to the correct product/page.
      processedUrl: computed(() => {
        const cachedUrl = blok.link?.cached_url
        if (!cachedUrl) {
          return cachedUrl
        }

        const parts = cachedUrl.split('/').filter(Boolean)
        const lastSegment = parts[parts.length - 1]

        if (locales.find((locale) => locale.code === lastSegment)) {
          return ''
        }

        return cachedUrl
      }),
      showLink,
      marginClasses,
    }
  },
})
</script>
